import type { UIComponent } from '../schemas/base';
import type { GenUIEvent } from './events';

/**
 * Render node types for the render tree
 */
export type RenderNode =
    | { kind: 'text'; content: string }
    | { kind: 'component'; component: UIComponent };

/**
 * State for the GenUI reducer
 */
export interface GenUIState {
    nodes: RenderNode[];
    isStreaming: boolean;
    error: Error | null;
}

/**
 * Initial state
 */
export const initialState: GenUIState = {
    nodes: [],
    isStreaming: false,
    error: null,
};

/**
 * Action types for the reducer
 */
export type GenUIAction =
    | { type: 'START_STREAMING' }
    | { type: 'EVENT'; event: GenUIEvent }
    | { type: 'STOP_STREAMING' }
    | { type: 'SET_ERROR'; error: Error }
    | { type: 'RESET' };

/**
 * Reducer for building the render tree from events
 * 
 * Rules:
 * - Text deltas append to the last text node (or create new one)
 * - Components are added as new nodes
 * - Order is strictly preserved
 */
export function genUIReducer(state: GenUIState, action: GenUIAction): GenUIState {
    switch (action.type) {
        case 'START_STREAMING':
            return {
                ...state,
                isStreaming: true,
                error: null,
            };

        case 'EVENT': {
            const { event } = action;

            switch (event.type) {
                case 'text_delta': {
                    const nodes = [...state.nodes];
                    const lastNode = nodes[nodes.length - 1];

                    // Append to last text node or create new one
                    if (lastNode && lastNode.kind === 'text') {
                        nodes[nodes.length - 1] = {
                            kind: 'text',
                            content: lastNode.content + event.value,
                        };
                    } else {
                        nodes.push({ kind: 'text', content: event.value });
                    }

                    return { ...state, nodes };
                }

                case 'component': {
                    // Add component as new node
                    return {
                        ...state,
                        nodes: [
                            ...state.nodes,
                            { kind: 'component', component: event.payload },
                        ],
                    };
                }

                case 'done':
                    return {
                        ...state,
                        isStreaming: false,
                    };

                default:
                    return state;
            }
        }

        case 'STOP_STREAMING':
            return {
                ...state,
                isStreaming: false,
            };

        case 'SET_ERROR':
            return {
                ...state,
                isStreaming: false,
                error: action.error,
            };

        case 'RESET':
            return initialState;

        default:
            return state;
    }
}
